import React, {
  CSSProperties,
  ForwardedRef,
  HTMLAttributes,
  forwardRef,
  useEffect,
  useState,
} from "react";
import ResizeObserver from "resize-observer-polyfill";
import { classNames } from "./classNames";
import { isThemePreviewMode } from "utils/theme-preview-storage";

type Listener = (height: number) => void;

const DEFAULT_HEIGHT = "52vw";
const STYLED_COURT_SELECTOR = ".styled-court";

const listeners = new Set<Listener>();
let cachedHeight: number | null = null;
let observer: ResizeObserver | null = null;
let observedElement: Element | null = null;
let retryHandle: number | null = null;

const notifyListeners = (height: number) => {
  cachedHeight = height;
  listeners.forEach((listener) => listener(height));
};

const cleanupObserver = () => {
  if (observer && observedElement) {
    observer.unobserve(observedElement);
  }
  if (observer) {
    observer.disconnect();
  }
  observer = null;
  observedElement = null;
  if (retryHandle != null && typeof window !== "undefined") {
    window.cancelAnimationFrame(retryHandle);
    retryHandle = null;
  }
};

const scheduleRetry = () => {
  if (retryHandle != null || typeof window === "undefined") return;
  retryHandle = window.requestAnimationFrame(() => {
    retryHandle = null;
    if (!observer) {
      initializeObserver();
    }
  });
};

const initializeObserver = () => {
  if (typeof document === "undefined") return;
  const target = document.querySelector(STYLED_COURT_SELECTOR);
  if (!target) {
    scheduleRetry();
    return;
  }

  observedElement = target;
  observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
    entries.forEach((entry) => {
      if (entry.target !== observedElement) return;
      const nextHeight = entry.contentRect?.height;
      if (!Number.isFinite(nextHeight) || nextHeight <= 0) return;
      notifyListeners(nextHeight);
    });
  });

  observer.observe(target);
  const initialRect = (target as HTMLElement).getBoundingClientRect();
  if (Number.isFinite(initialRect.height) && initialRect.height > 0) {
    notifyListeners(initialRect.height);
  }
};

const subscribeToHeight = (listener: Listener) => {
  listeners.add(listener);
  if (cachedHeight != null) {
    listener(cachedHeight);
  }
  if (!observer) {
    initializeObserver();
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      cleanupObserver();
    }
  };
};

export const useSimulatorTabHeight = () => {
  const [height, setHeight] = useState<number | null>(() => cachedHeight);

  useEffect(() => {
    const unsubscribe = subscribeToHeight((next) => setHeight(next));
    return () => {
      unsubscribe();
    };
  }, []);

  return height;
};

type FullHeightTabPanelProps = HTMLAttributes<HTMLDivElement> & {
  scrollable?: boolean;
};

export const FullHeightTabPanel = forwardRef(function FullHeightTabPanel(
  { className, style, scrollable = true, ...rest }: FullHeightTabPanelProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const measuredHeight = useSimulatorTabHeight();
  const resolvedStyle: CSSProperties = {
    ...style,
  };

  const desiredHeight =
    measuredHeight != null ? `${measuredHeight}px` : DEFAULT_HEIGHT;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isThemePreviewMode()) return;
    let isIframe = false;
    try {
      isIframe = window.self !== window.top;
    } catch {
      isIframe = true;
    }

    if (!isIframe) return;
    const { documentElement } = window.document;
    documentElement.style.setProperty(
      "--simulator-panel-height",
      desiredHeight
    );

    return () => {
      documentElement.style.setProperty(
        "--simulator-panel-height",
        DEFAULT_HEIGHT
      );
    };
  }, [desiredHeight]);

  if (typeof resolvedStyle.height === "undefined") {
    resolvedStyle.height = desiredHeight;
  }

  if (typeof resolvedStyle.maxHeight === "undefined") {
    resolvedStyle.maxHeight = desiredHeight;
  }

  return (
    <div
      ref={ref}
      className={classNames(
        "w-full",
        scrollable ? "overflow-y-auto" : undefined,
        className
      )}
      style={resolvedStyle}
      {...rest}
    />
  );
});
